/**
 * Server va WebApp o'rtasidagi API shartnomasi.
 *
 * Bu tiplar YAGONA manba. Server javobni shu shaklda yuboradi, WebApp shu shaklda
 * o'qiydi. Ikki tomon alohida-alohida tip yozsa, ular vaqt o'tib bir-biridan
 * uzoqlashadi va xato faqat ishga tushganda bilinadi.
 */

import type {
  BloggerStage,
  Lang,
  PayoutStatus,
  PayoutType,
  RejectReason,
  VideoStage,
} from './enums'

/** Loyihaning yagona versiya raqami — server ham, WebApp ham shundan oladi. */
export const APP_VERSION = '1.0.0'

/** Xatolik javobining standart shakli: `{ error: { message, statusCode } }`. */
export interface ApiErrorBody {
  error: {
    message: string
    statusCode: number
    /** Faqat autentifikatsiya xatolarida — WebApp shunga qarab ekran tanlaydi. */
    code?: AuthErrorCode
  }
}

/** Baza ulanishining holati — `/api/health` javobining bir qismi. */
export interface DbHealth {
  ok: boolean
  latencyMs: number
  error?: string
}

/**
 * `GET /api/health` javobi.
 *
 * `db` ixtiyoriy: baza sozlanmagan bo'lsa (dev rejimida `DATABASE_URL` yo'q) u umuman
 * bo'lmaydi. Diqqat: baza yiqilganda ham endpoint 200 va `ok: true` qaytaradi —
 * `ok` XIZMAT holatini bildiradi. Docker HEALTHCHECK shu endpointga qaraydi, va
 * bazadagi bir soniyalik uzilish konteynerni o'chirib-yoqish siklini boshlab
 * yubormasligi kerak. Baza muammosi `db.ok` da ko'rinadi.
 */
export interface HealthResponse {
  ok: boolean
  service: string
  version: string
  uptime: number
  env: string
  timestamp: string
  db?: DbHealth
}

/**
 * Autentifikatsiya xatolarining kodlari.
 *
 * WebApp ularga qarab TURLI ekran ko'rsatadi: sessiya eskirgan bo'lsa "panelni
 * qayta oching" deydi, admin bo'lmasa "ruxsat yo'q" deydi. Matnni HTTP status
 * kodidan taxmin qilish o'rniga aniq kod yuboriladi — 403 ikkala holatda ham
 * chiqishi mumkin.
 */
export const AuthErrorCode = {
  MISSING: 'AUTH_MISSING', // Authorization sarlavhasi umuman yo'q
  INVALID: 'AUTH_INVALID', // HMAC imzo mos kelmadi (soxta yoki buzilgan)
  EXPIRED: 'AUTH_EXPIRED', // auth_date juda eski — panelni qayta ochish kerak
  NOT_ADMIN: 'AUTH_NOT_ADMIN', // imzo to'g'ri, lekin foydalanuvchi admin emas
} as const
export type AuthErrorCode = (typeof AuthErrorCode)[keyof typeof AuthErrorCode]

/** Admin panelining bosh sahifasi — `GET /api/dashboard`. */
export interface DashboardResponse {
  /** Kundalik ish navbati — adminning ertalab ko'radigan raqamlari. */
  queue: {
    /** Bugun tekshirilishi kerak bo'lgan videolar (nextCheckAt = bugun). */
    dueToday: number
    /** Muddati o'tib ketgan tekshiruvlar. */
    overdue: number
    /** Moderatsiyani kutayotgan videolar. */
    moderation: number
    /** Tasdiqlangan, lekin havola kelmagan videolar. */
    awaitingLink: number
    /** Havolasi kelgan, lekin admin tekshirmagan videolar. */
    unverifiedLinks: number
    /** Shundan 48 soatdan oshgani — kechiktirilgan tekshiruv. */
    unverifiedLinksOverdue: number
    /** Ko'rib chiqilmagan yangi arizalar. */
    newApplications: number
  }

  /** To'lovga tayyor summa — hisobi yopilgan, lekin to'lanmagan videolar. */
  payouts: {
    /** So'mda. */
    readyAmount: number
    /** Nechta blogerga tegishli. */
    readyBloggers: number
    /** Bloger o'zi so'rov yuborgan, admin hali to'lamagan. */
    pendingRequests: number
  }

  /** Joriy oy ko'rsatkichlari. */
  month: {
    activeBloggers: number
    newVideos: number
    totalViews: number
    /** Shu oyda yopilgan hisoblar bo'yicha majburiyat, so'mda. */
    committedAmount: number
  }

  /**
   * Joriy oy to'lov byudjeti. `amount === null` — bu oyga byudjet o'rnatilmagan (cheklov yo'q).
   * `paidThisMonth` — shu oyda haqiqatda to'langan (Payout PAID, paidAt shu oy). `remaining` —
   * qolgan (`amount - paidThisMonth`), byudjet o'rnatilmagan bo'lsa `null`.
   */
  budget: {
    amount: number | null
    paidThisMonth: number
    remaining: number | null
  }
}

// ─────────────────────── Blogerlar (Bosqich 6) ───────────────────────

/** Sahifalangan ro'yxat javobining umumiy shakli. */
export interface Paginated<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
  /** Yana sahifa bormi — `page * pageSize < total` hisobini frontendda takrorlamaslik uchun. */
  hasMore: boolean
}

/**
 * Ro'yxatdagi bitta bloger.
 *
 * Diqqat: bu yerda karta raqami YO'Q. Ro'yxat ekranida u kerak emas, va maxfiy
 * ma'lumotni kerak bo'lmagan joyga yubormaslik — eng oddiy himoya (docs/05).
 */
export interface BloggerListItem {
  id: number
  fullName: string
  instagramUsername: string
  followers: number
  phone: string
  stage: BloggerStage
  /** Tasdiqlangan (REJECTED/BLOCKED bo'lmagan) videolar soni. */
  videoCount: number
  /** Jami ishlangan summa, so'mda (hisobi yopilgan videolar bo'yicha). */
  totalEarned: number
  /** Blogerga berilgan (biriktirilgan) kitoblar soni — `BookAssignment` qatorlari. */
  booksCount: number
  createdAt: string
}

/** Bloger kartochkasidagi moliyaviy xulosa — barchasi hisoblanadi, saqlanmaydi. */
export interface BloggerFinance {
  /** Hisobi yopilgan, hali to'lanmagan — "kutilayotgan". */
  pendingAmount: number
  /** Hali tekshiruvda, yakunlanmagan — taxminiy. */
  inProgressAmount: number
  /** Jami to'langan. */
  paidAmount: number
}

/** Bloger kartochkasidagi qisqa video qatori. */
export interface BloggerVideoSummary {
  id: number
  stage: VideoStage
  instagramUrl: string | null
  /** Oxirgi ma'lum prosmotr (yakuniy bo'lmasa — oraliq tekshiruvdan). */
  views: number | null
  amount: number | null
  createdAt: string
}

/** Kartochka izohi. */
export interface CommentItem {
  id: number
  text: string
  authorId: string
  createdAt: string
}

/**
 * Blogerga biriktirilgan bitta kitob — `BloggerDetail.bookAssignments` elementi.
 *
 * `bookTitle`/`price` biriktirilgan paytda NUSXALANGAN (katalogda kitob keyin o'zgarsa yoki
 * o'chirilsa ham tarix buzilmaydi). `bookId` — katalogga jonli havola; kitob o'chirilgan
 * bo'lsa `null` (nusxa maydonlari baribir qoladi).
 */
export interface BookAssignmentItem {
  id: number
  bookId: number | null
  bookTitle: string
  /** Biriktirilgan paytdagi narx, so'mda. */
  price: number
  note: string | null
  assignedAt: string
  /** Biriktirgan admin telegramId (Number'ga o'girilgan); `null` = tizim. */
  assignedBy: number | null
}

/** Bloger kartochkasi — `GET /api/bloggers/:id`. */
export interface BloggerDetail extends BloggerListItem {
  telegramId: string
  telegramUsername: string | null
  lang: Lang
  /** Maskalangan: `8600 **** **** 1234`. To'liq raqam alohida endpoint orqali. */
  cardNumberMasked: string | null
  cardHolder: string | null
  rejectReason: RejectReason | null
  rejectNote: string | null
  rejectedAt: string | null
  blockedAt: string | null
  approvedAt: string | null
  botBlocked: boolean
  finance: BloggerFinance
  videos: BloggerVideoSummary[]
  comments: CommentItem[]
  /** Blogerga berilgan kitoblar — eng yangisi tepada. */
  bookAssignments: BookAssignmentItem[]
}

/** `GET /api/bloggers` so'rov parametrlari. */
export interface BloggerListQuery {
  stage?: BloggerStage
  /** Ism, Instagram username yoki telefon bo'yicha qidiruv. */
  q?: string
  page?: number
  pageSize?: number
  sort?: 'createdAt' | 'followers' | 'totalEarned' | 'fullName'
  order?: 'asc' | 'desc'
}

/** Ariza bo'yicha qaror — `POST /api/bloggers/:id/review`. */
export interface BloggerReviewRequest {
  approved: boolean
  /** Rad etilganda majburiy. */
  reason?: RejectReason
  /** `OTHER` tanlanganda majburiy; boshqa holatda ixtiyoriy qo'shimcha. */
  note?: string
  /**
   * Tasdiqlashda blogerga biriktiriladigan kitob — IXTIYORIY (katalogdagi `Book.id`).
   * Faqat `approved: true` bo'lganda hisobga olinadi. Kitob hali kelishilmagan bo'lsa
   * bo'sh qoldiriladi va keyin kartochkadan qo'shiladi (`POST /:id/books`).
   */
  bookId?: number
}

/** Blogerga kitob biriktirish (kartochkadan) — `POST /api/bloggers/:id/books`. */
export interface BloggerAssignBookRequest {
  /** Katalogdagi `Book.id`. */
  bookId: number
  /** Ixtiyoriy izoh (masalan "2-nusxa"). */
  note?: string
}

/** Bloklash/blokdan chiqarish — `POST /api/bloggers/:id/block`. */
export interface BloggerBlockRequest {
  blocked: boolean
  note?: string
}

/** Har bir varonka ustunidagi soni — kanban sarlavhalari uchun. */
export type BloggerStageCounts = Record<BloggerStage, number>

// ─────────────────────── Videolar (Bosqich 7+) ───────────────────────

/**
 * Video kartochkasining "shoshilinchlik" holati — kanban rangi shunga bog'liq.
 * `checkUrgency()` (dates.ts) qaytaradigan qiymatlar bilan bir xil, ustiga
 * `finalized` qo'shilgan: hisobi yopilgan, to'lov kutayotgan kartochka.
 */
export type VideoUrgency = 'overdue' | 'today' | 'upcoming' | 'none' | 'finalized'

/** Kanban/ro'yxatdagi bitta video. */
export interface VideoListItem {
  id: number
  stage: VideoStage
  urgency: VideoUrgency

  /** Kartochkada blogerni ko'rsatish uchun — alohida so'rov qilmaslik uchun. */
  blogger: {
    id: number
    fullName: string
    instagramUsername: string
  }

  instagramUrl: string | null
  /** Admin havolani ochib tekshirganmi. `false` + eski `postedAt` = kechikkan tekshiruv. */
  linkVerified: boolean

  /**
   * Admin videoni tasdiqlagan vaqt — `null` bo'lsa hali ko'rilmagan.
   *
   * MUHIM: tasdiqlangan, lekin havolasi kelmagan video ham `MODERATION` bosqichida
   * qoladi (docs/05 dagi 5 varonkali murosaning oqibati). Ya'ni faqat `stage` bo'yicha
   * "ko'rilmagan" va "havola kutilmoqda" holatlarini AJRATIB BO'LMAYDI — ularni
   * ajratadigan yagona maydon shu. Usiz admin allaqachon tasdiqlagan kartochkani
   * qayta-qayta ochib ko'raveradi.
   */
  moderatedAt: string | null

  /** Oxirgi ma'lum prosmotr. */
  views: number | null
  /** Shu prosmotrga mos summa, so'mda. */
  amount: number | null

  /** Tekshiruv kiritish kutilmoqdami (hali kiritilmagan, hisob yopilmagan). */
  checkPending: boolean
  nextCheckAt: string | null
  /** Muddatga necha kun qolgani; manfiy = kechikkan. */
  daysUntilCheck: number | null

  postedAt: string | null
  createdAt: string
}

/** Video kartochkasidagi tekshiruv yozuvi (video uchun bitta). */
export interface VideoCheckItem {
  views: number
  amount: number
  checkedAt: string
  source: string
  /**
   * Prosmotr skrinshoti — Telegram `file_id` (faqat bahsli holatda so'raladi, ko'pincha
   * `null`). WebApp uni `GET /api/files/:fileId` (auth bilan proxy) orqali ko'rsatadi.
   */
  screenshotFileId: string | null
}

/** Video kartochkasi — `GET /api/videos/:id`. */
export interface VideoDetail extends VideoListItem {
  /**
   * Arxiv kanaldagi videoni ochish havolasi (`t.me/c/<kanal>/<xabar>`).
   *
   * Bot API'ning `getFile` cheklovi 20 MB — telefonda yozilgan reels ko'pincha
   * bundan katta. Shuning uchun video panelda ichki pleyerda emas, Telegram'ning
   * o'zida ochiladi (docs/02-trd.md). Forward qilinmagan bo'lsa `null`.
   */
  archiveUrl: string | null

  /** Bloger haqida qo'shimcha — kartochkada ko'rsatiladi. */
  bloggerPhone: string
  bloggerFollowers: number

  /** Stavka video `POSTED` ga o'tganda muzlatiladi. */
  ratePer1000: number | null

  firstCheckAt: string | null
  finalViews: number | null
  finalAmount: number | null
  finalizedAt: string | null

  // `moderatedAt` — `VideoListItem` dan meros olinadi, bu yerda takrorlanmaydi.
  rejectReason: RejectReason | null
  rejectNote: string | null

  checks: VideoCheckItem[]
  comments: CommentItem[]
}

/** `GET /api/videos` so'rov parametrlari. */
export interface VideoListQuery {
  stage?: VideoStage
  /** Tez filtrlar (docs/03 "Tez filtrlar"). */
  due?: 'today' | 'overdue' | 'ready' | 'unverified'
  /** Bloger ismi yoki Instagram username bo'yicha. */
  q?: string
  bloggerId?: number
  page?: number
  pageSize?: number
}

/** Har bir varonka ustunidagi soni. */
export type VideoStageCounts = Record<VideoStage, number>

/** Kanban yuqorisidagi tez filtr tugmalaridagi sonlar. */
export interface VideoQuickCounts {
  today: number
  overdue: number
  ready: number
  unverified: number
}

/**
 * Havola tekshiruvi — `POST /api/videos/:id/verify-link`.
 *
 * Admin havolani ochib ko'rgach: to'g'ri bo'lsa `ok: true` (kartochkadan 🔴 belgi
 * yo'qoladi), noto'g'ri/o'chirilgan bo'lsa `ok: false` — video rad etiladi va
 * blogerga sabab bilan xabar boradi.
 */
export interface VideoVerifyLinkRequest {
  ok: boolean
  /** `ok: false` bo'lganda majburiy — odatda `LINK_DEAD` yoki `IRRELEVANT_CONTENT`. */
  reason?: RejectReason
  note?: string
}

/**
 * Prosmotr kiritish — `POST /api/videos/:id/checks`.
 *
 * Video uchun BITTA tekshiruv: 15-kunda kiritiladi, hisobni DARHOL yopadi va summani
 * blogerning balansiga qo'shadi — qaytarib bo'lmaydigan amal.
 */
export interface VideoCheckRequest {
  views: number
}

/** Kiritilgan prosmotrni TUZATISH — `PATCH /api/videos/:id/check`. */
export interface VideoCheckPatchRequest {
  views: number
  /** Nima uchun tuzatilgani — bahs chiqsa `ActivityLog` da qoladi. */
  note?: string
}

/** Tekshiruv saqlangandan keyingi javob. */
export interface VideoCheckResponse {
  /** Yangilangan video — kartochka darhol qayta chizilishi uchun. */
  video: VideoDetail
  /** Shu tekshiruv bo'yicha hisoblangan summa, so'mda. */
  amount: number
  /** Yakuniy tekshiruv bo'lsa — hisob yopildi va balansga qo'shildi. */
  finalized: boolean
  /**
   * Yakuniy tekshiruvdan keyin blogerda karta ma'lumoti yo'qmi.
   * `true` bo'lsa bot avtomatik ravishda karta so'raydi — admin bilishi kerak,
   * chunki 5-sanadagi to'lov ro'yxatida bu bloger ⚠️ bilan chiqadi.
   */
  cardRequested: boolean
}

/** Moderatsiya qarori — `POST /api/videos/:id/moderate`. */
export interface VideoModerateRequest {
  approved: boolean
  /** Rad etilganda majburiy. */
  reason?: RejectReason
  /** `OTHER` tanlanganda majburiy. */
  note?: string
}

// ─────────────────────── To'lovlar (Bosqich 10) ───────────────────────

/**
 * To'lovga tayyor bitta bloger — `GET /api/payouts/ready`.
 *
 * "Tayyor" degani: hisobi yopilgan (`finalizedAt`), lekin hali to'lanmagan
 * (`payoutId = null`) videolari bor. Bu SAQLANMAYDI — har safar hisoblanadi
 * (docs/05 prinsip #2), shuning uchun raqam hech qachon "surilib" ketmaydi.
 */
export interface PayoutReadyItem {
  bloggerId: number
  fullName: string
  instagramUsername: string
  /** To'lanishi kerak bo'lgan summa, so'mda. */
  amount: number
  /** Necha ta video shu summani tashkil qiladi. */
  videoCount: number
  /** Maskalangan karta — to'liq raqam alohida, auditlanadigan endpoint orqali. */
  cardNumberMasked: string | null
  cardHolder: string | null
  /**
   * Karta yo'q bo'lsa to'lov YARATIB BO'LMAYDI — ro'yxatda ⚠️ bilan ko'rsatiladi va
   * ommaviy to'lovda o'tkazib yuboriladi (docs/03 "To'lovga tayyor" bo'limi).
   */
  hasCard: boolean
  /** Bloger o'zi so'rov yuborganmi (kutayotgan `REQUESTED` to'lov bormi). */
  hasPendingRequest: boolean
}

/** To'lovlar ro'yxatidagi bitta yozuv — so'rovlar va tarix uchun umumiy. */
export interface PayoutListItem {
  id: number
  bloggerId: number
  bloggerName: string
  instagramUsername: string
  type: PayoutType
  status: PayoutStatus
  amount: number
  videoCount: number
  /** To'lov paytida NUSXALANGAN karta — bloger keyin o'zgartirsa ham tarixda qoladi. */
  cardNumberMasked: string
  cardHolder: string
  requestedAt: string
  paidAt: string | null
  note: string | null
}

/** Blogerning moliyaviy holati — bot "Balansim" ekrani va API uchun umumiy. */
export interface BloggerBalance {
  /** Hisobi yopilgan, to'lanmagan — hoziroq to'lanishi mumkin bo'lgan summa. */
  pendingAmount: number
  /** Hali tekshiruvda — taxminiy, o'zgarishi mumkin. */
  inProgressAmount: number
  /** Jami to'langan (barcha vaqt). */
  paidAmount: number
  /** Bloger so'rov yubora oladimi (pendingAmount >= chegara va kutayotgan so'rov yo'q). */
  canRequest: boolean
  /** Joriy chegara, so'mda — xabarda ko'rsatish uchun. */
  threshold: number
  /** Kutayotgan so'rov bormi. */
  hasPendingRequest: boolean
}

/** To'lov yaratish — `POST /api/payouts`. */
export interface PayoutCreateRequest {
  bloggerId: number
  type: PayoutType
  note?: string
}

/** Ommaviy to'lov yaratish — `POST /api/payouts/bulk`. */
export interface PayoutBulkCreateRequest {
  bloggerIds: number[]
  type: PayoutType
  note?: string
}

/** Ommaviy amal natijasi — qaysi biri o'tdi, qaysi biri nega o'tmadi. */
export interface PayoutBulkResult {
  created: number
  skipped: Array<{ bloggerId: number; reason: 'no_card' | 'no_balance' | 'already_requested' }>
  totalAmount: number
}

/** To'landi deb belgilash — `POST /api/payouts/:id/paid`. */
export interface PayoutMarkPaidRequest {
  note?: string
}

/** So'rovni bekor qilish — `POST /api/payouts/:id/cancel`. */
export interface PayoutCancelRequest {
  /** Blogerga sabab ko'rsatiladi, shuning uchun majburiy. */
  note: string
}

/** To'lovlar sahifasining umumiy hisoblagichlari. */
export interface PayoutSummary {
  /** Bloger so'ragan, to'lanmagan. */
  pendingRequests: number
  pendingRequestsAmount: number
  /** To'lovga tayyor (so'rovsiz ham). */
  readyBloggers: number
  readyAmount: number
  /** Shundan kartasi yo'qligi uchun to'lab bo'lmaydigani. */
  blockedByMissingCard: number
}

// ─────────────────────── Oylik byudjet (kampaniya byudjeti) ───────────────────────

/**
 * Bir oyning to'lov byudjeti holati — `GET /api/budget`.
 *
 * `amount === null` — bu oyga byudjet o'rnatilmagan (cheklov yo'q). `spent` — shu oyda
 * to'langan (Payout PAID). `remaining` — qolgan (`amount - spent`), byudjetsiz `null`.
 * `committed` — shu oy yopilgan hisoblar summasi (majburiyat). `outstanding` — jami
 * to'lanmagan tayyor (backlog): byudjet tugasa to'planib boradigan qoldiq.
 */
export interface MonthlyBudgetStatus {
  year: number
  month: number
  amount: number | null
  spent: number
  remaining: number | null
  committed: number
  outstanding: number
}

/** Byudjet o'rnatish/tahrirlash — `PUT /api/budget` (faqat super admin). */
export interface MonthlyBudgetSetRequest {
  year: number
  month: number
  amount: number
}

/** Byudjet tarixidagi bitta oy — `GET /api/budget/history`. */
export interface MonthlyBudgetHistoryItem {
  year: number
  month: number
  amount: number
  spent: number
}

// ─────────────────────── Broadcast (Bosqich 11) ───────────────────────

/**
 * Broadcast qabul qiluvchilarini tanlash usuli.
 *
 * Uchta usul ATAYLAB alohida: "barchasi" va "bosqich bo'yicha" server tomonda
 * so'rov bilan yechiladi (admin 400 ta ID yubormasin), "qo'lda" esa aniq ro'yxat
 * bo'lgani uchun mijoz tomondan keladi.
 */
export type BroadcastAudience =
  | { kind: 'all' }
  | { kind: 'stage'; stage: BloggerStage }
  | { kind: 'manual'; bloggerIds: number[] }

/** Broadcast holati — `Broadcast.status` ustunidagi qiymatlar. */
export type BroadcastStatus = 'pending' | 'sending' | 'done' | 'failed'

/** Yuborishdan oldin "nechta odamga ketadi" — `POST /api/broadcasts/preview`. */
export interface BroadcastPreviewRequest {
  audience: BroadcastAudience
}

/**
 * Ko'rib chiqish natijasi. `botBlocked` alohida ko'rsatiladi, chunki admin
 * "442 ta" degan raqamni ko'rib, keyin "438 yetkazildi" ni xato deb o'ylamasligi kerak —
 * bloklaganlar OLDINDAN ma'lum.
 */
export interface BroadcastPreviewResult {
  /** Jami mos keluvchi blogerlar. */
  total: number
  /** Shundan botni bloklaganlari — ularga urinib ham ko'rilmaydi. */
  botBlocked: number
  /** Haqiqatda xabar ketadigan son (`total - botBlocked`). */
  deliverable: number
}

/** Broadcast yaratish va darhol yuborishni boshlash — `POST /api/broadcasts`. */
export interface BroadcastCreateRequest {
  audience: BroadcastAudience
  textUz: string
  textRu: string
  /** Telegram `file_id` — rasm yoki video (ixtiyoriy). */
  fileId?: string
  /** `fileId` qaysi turdagi media ekani — yuborishda `sendPhoto`/`sendVideo` tanlash uchun. */
  fileType?: 'photo' | 'video'
}

/** Ro'yxat va progress uchun bitta broadcast yozuvi. */
export interface BroadcastListItem {
  id: number
  textUz: string
  textRu: string
  fileId: string | null
  fileType: 'photo' | 'video' | null
  targetStage: BloggerStage | null
  totalCount: number
  sentCount: number
  failedCount: number
  status: BroadcastStatus
  createdAt: string
  finishedAt: string | null
}

/** Broadcast ro'yxati — `GET /api/broadcasts`. Boshqa ro'yxatlar kabi TEKIS shakl. */
export interface BroadcastListResult {
  items: BroadcastListItem[]
  total: number
  page: number
  pageSize: number
}

// ─────────────────────── Sozlamalar (Bosqich 11) ───────────────────────

/**
 * Barcha sozlamalar — `GET /api/settings`.
 *
 * Kalitlar `SETTING_KEYS` bilan bir xil (`constants.ts`), lekin bu yerda tip
 * darajasida qat'iy: raqamli sozlamalar `number`, `views_provider` esa satr.
 */
/**
 * Stavka bosqichi (zinapoyali stavka) — `SettingsResponse.rateTiers` elementi.
 *
 * `views` — shu bosqichga tegishli prosmotr ENI (band kengligi). Bosqichlar tartib bilan
 * qo'llanadi: birinchisi 0..views1, keyingisi undan keyingi views2, va h.k. OXIRGI bosqichning
 * `views` eni E'TIBORSIZ — u "qolgan barchasi"ni qamrab oladi. `ratePer1000` — shu banddagi
 * 1000 prosmotr uchun so'm. Hisob `calculateTieredAmount()` (`format.ts`) orqali.
 */
export interface RateTier {
  views: number
  ratePer1000: number
}

export interface SettingsResponse {
  /**
   * Bosqichli stavka jadvali (kamida 1 bosqich). Bitta bosqich = eski tekis stavka. Yangi
   * videolar POSTED bo'lganda shu jadval `Video.rateTiers`ga muzlatiladi.
   */
  rateTiers: RateTier[]
  /**
   * Eski tekis stavka — endi faqat zaxira (bosqichlar yo'q bo'lsa) va eski (bosqichlarsiz
   * muzlatilgan) videolar uchun. Yangi hisob `rateTiers` bilan.
   */
  ratePer1000: number
  autoPayoutThreshold: number
  scheduledPayoutDay: number
  firstCheckDays: number
  minFollowers: number
  reapplyAfterDays: number
  linkWaitDays: number
  viewsProvider: string
}

/**
 * Sozlamalarni yangilash — `PATCH /api/settings` (faqat super admin).
 * Barcha maydonlar ixtiyoriy: admin faqat o'zgartirganini yuboradi.
 */
export type SettingsUpdateRequest = Partial<SettingsResponse>

/**
 * Adminlar ro'yxati — `GET /api/admins`.
 *
 * `Admin` jadvalining yagona kaliti — `telegramId` (`BigInt @id`), alohida raqamli
 * `id` ustuni YO'Q. Shuning uchun bu yerda ham sun'iy `id` maydoni yaratilmaydi:
 * `DELETE /api/admins/:id` dagi `:id` ham aynan `telegramId`.
 *
 * `name` — sxemada `String?`, ya'ni admin BotFather orqali qo'shilib, ismi hali
 * ma'lum bo'lmasligi mumkin. Bo'shliqni `''` bilan yashirmaymiz: WebApp uni
 * "Ism ko'rsatilmagan" deb ko'rsatishi kerak.
 */
export interface AdminListItem {
  telegramId: number
  name: string | null
  isSuper: boolean
  createdAt: string
}

/** Admin qo'shish — `POST /api/admins` (faqat super admin). */
export interface AdminCreateRequest {
  telegramId: number
  name: string
}

// ─────────────────────── Kitoblar (katalog) ───────────────────────

/**
 * Katalogdagi bitta kitob — `GET /api/books`.
 *
 * `price` — so'mda (server BigInt saqlaydi, JSON'da Number bo'lib keladi, `formatSom`
 * bilan ko'rsatiladi). `description` ixtiyoriy (`null` bo'lishi mumkin).
 */
export interface BookItem {
  id: number
  title: string
  author: string
  price: number
  description: string | null
  createdAt: string
}

/** Kitob qo'shish — `POST /api/books` (faqat super admin). */
export interface BookCreateRequest {
  title: string
  author: string
  price: number
  description?: string
}

/**
 * Kitobni tahrirlash — `PATCH /api/books/:id` (faqat super admin). Barcha maydonlar
 * ixtiyoriy: admin faqat o'zgartirganini yuboradi.
 */
export type BookUpdateRequest = Partial<BookCreateRequest>

// ─────────────────────── Chat (admin ↔ bloger) ───────────────────────

/** Chat xabarining muallifi — admin yoki bloger. `ChatMessage.sender` bilan bir xil. */
export type ChatSender = 'ADMIN' | 'BLOGGER'

/**
 * Bitta chat xabari — `GET /api/chat/:bloggerId/messages` elementi.
 *
 * `authorId` — admin telegramId (Number'ga o'girilgan), bloger yozgan xabarda `null`.
 * `authorName` — admin ismi (`Admin.name`), bloger xabarida `null`.
 */
export interface ChatMessageItem {
  id: number
  sender: ChatSender
  text: string
  authorId: number | null
  authorName: string | null
  createdAt: string
}

/**
 * Chat bo'limidagi bitta suhbat qatori — `GET /api/chat/conversations` elementi. Har blogerga
 * bitta suhbat; `unreadCount` — admin hali o'qimagan (blogerdan kelgan) xabarlar soni.
 */
export interface ChatConversationItem {
  bloggerId: number
  fullName: string
  instagramUsername: string
  stage: BloggerStage
  lastText: string
  lastAt: string
  /** Oxirgi xabarni kim yozgan — ro'yxatda "siz:" prefiksi uchun. */
  lastSender: ChatSender
  unreadCount: number
}

/** Admin chat xabari yuborishi — `POST /api/chat/:bloggerId/messages`. */
export interface ChatSendRequest {
  text: string
}

/** Menyudagi umumiy o'qilmagan belgisi — `GET /api/chat/unread-count`. */
export interface ChatUnreadCountResponse {
  unread: number
}

// ─────────────────────── Brauzerda kirish (Login Widget) ───────────────────────
//
// Panel ikki xil muhitda ochiladi:
//   1. Telegram Mini App ichida — `initData` HMAC tekshiruvi (asosiy yo'l, o'zgarmadi)
//   2. Oddiy brauzerda — Telegram Login Widget + sessiya cookie (shu bo'lim)
//
// Ikkalasi ham BIR XIL ishonch manbasiga tayanadi: bot tokeni bilan tekshiriladigan
// Telegram imzosi. `docs/02-trd.md` dagi "Alohida login/parol tizimi yo'q" qarori
// buzilmaydi — parol hech qayerda saqlanmaydi va so'ralmaydi.

/**
 * Telegram Login Widget qaytaradigan ma'lumot — Telegram'ning o'zi shu shaklda beradi,
 * maydon nomlari (`snake_case`) Telegram hujjatidagidek qoladi.
 *
 * ⚠️ Imzo tekshiruvi `initData` dan FARQ QILADI: bu yerda maxfiy kalit
 * `SHA256(bot_token)`, `initData` da esa `HMAC_SHA256("WebAppData", bot_token)`.
 * Ikkalasini adashtirish — imzo har doim noto'g'ri chiqishiga olib keladi.
 */
export interface TelegramLoginPayload {
  id: number
  first_name: string
  last_name?: string
  username?: string
  photo_url?: string
  auth_date: number
  hash: string
}

/** Login sahifasi uchun ochiq sozlama — `GET /api/auth/config` (auth TALAB QILINMAYDI). */
export interface AuthConfigResponse {
  /** Widget'ga kerak: `data-telegram-login` atributi shu qiymatni oladi. */
  botUsername: string
}

/** Joriy sessiya egasi — `GET /api/auth/me`. */
export interface AuthMeResponse {
  telegramId: number
  name: string | null
  /**
   * Super admin huquqi. Bu maydon Bosqich 11 dagi vaqtinchalik yechimni ALMASHTIRADI:
   * u yerda WebApp joriy adminni aniqlash uchun `GET /api/admins` ro'yxatidan o'zini
   * qidirardi — bu esa oddiy admin uchun ishlamasdi va noto'g'ri javob berishi mumkin edi.
   */
  isSuper: boolean
}

// ─────────────────────── Amallar tarixi (docs/05: GET /api/activity) ───────────────────────

/**
 * Bitta ActivityLog yozuvi — `GET /api/activity` javob elementi.
 *
 * `oldValue`/`newValue` ataylab `unknown`: har amal o'z shaklini yozadi (masalan
 * `stage_changed` da `{stage}`, `views_entered` da `{views, amount}`) va WebApp ularni
 * odam o'qiydigan matnga o'zi o'giradi. Serverda qat'iy union qilish yozuv paytidagi
 * erkinlikni cheklab qo'yardi.
 */
export interface ActivityLogItem {
  id: number
  entityType: string
  entityId: number
  action: string
  oldValue: unknown
  newValue: unknown
  /** Admin telegramId (Number'ga o'girilgan); `null` = tizim (cron). */
  actorId: number | null
  actorType: 'admin' | 'system' | 'blogger'
  createdAt: string
  /**
   * Aktyor ismi — `Admin.name` dan (bo'lmasa null). Server qo'shib beradi, chunki
   * WebApp'da telegramId'dan ism topishning boshqa yo'li yo'q (adminlar ro'yxati
   * oddiy adminga ochiq bo'lsa-da, har log qatori uchun alohida so'rov isrof).
   */
  actorName: string | null
}

/** Amallar tarixi so'rovi natijasi — boshqa ro'yxatlar bilan bir xil TEKIS shakl. */
export interface ActivityListResult {
  items: ActivityLogItem[]
  total: number
  page: number
  pageSize: number
}

/** Kelgan JSON haqiqatan `ApiErrorBody` shaklidami — tip xavfsiz tekshiruv. */
export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null) return false
  const candidate = (value as { error?: unknown }).error
  if (typeof candidate !== 'object' || candidate === null) return false
  return typeof (candidate as { message?: unknown }).message === 'string'
}
